/**
 * U.S. regions behind the Job Code / Deployment ID.
 *
 * A job code is `<region letter>-<6 digit customer number>` (e.g. A-000101).
 * The letter comes from the region the client's state sits in; the number is
 * the client's position in the client base, assigned in the order clients were
 * added. Every appointment for a client therefore carries the same code.
 *
 * The region groupings are the ones on the U.S. Map Regions reference, which
 * covers all 50 states. D.C. is not on that map; it is grouped with the
 * Southeast, between Maryland and Virginia.
 *
 * The server keeps its own copy of this mapping in server/src/utils/regions.ts
 * (the two halves of the app do not share a module). Change both together.
 */

export type RegionKey =
  | 'pacific'
  | 'rocky-mountains'
  | 'southwest'
  | 'northeast'
  | 'southeast'
  | 'midwest'
  | 'noncontiguous';

export interface Region {
  key: RegionKey;
  /** The leading letter of the job code. */
  letter: string;
  label: string;
  /** Two-letter postal codes of every state in the region. */
  states: string[];
}

export const REGIONS: Region[] = [
  { key: 'pacific', letter: 'A', label: 'Pacific', states: ['WA', 'OR', 'CA'] },
  {
    key: 'rocky-mountains',
    letter: 'B',
    label: 'Rocky Mountains',
    states: ['MT', 'ID', 'WY', 'NV', 'UT', 'CO'],
  },
  { key: 'southwest', letter: 'C', label: 'Southwest', states: ['AZ', 'NM', 'OK', 'TX'] },
  {
    key: 'northeast',
    letter: 'D',
    label: 'Northeast',
    states: ['ME', 'NH', 'VT', 'MA', 'RI', 'CT', 'NY', 'NJ', 'PA'],
  },
  {
    key: 'southeast',
    letter: 'E',
    label: 'Southeast',
    states: ['DE', 'MD', 'DC', 'WV', 'VA', 'KY', 'TN', 'NC', 'SC', 'GA', 'FL', 'AL', 'MS', 'LA', 'AR'],
  },
  {
    key: 'midwest',
    letter: 'F',
    label: 'Midwest',
    states: ['ND', 'SD', 'NE', 'KS', 'MN', 'IA', 'MO', 'WI', 'IL', 'MI', 'IN', 'OH'],
  },
  { key: 'noncontiguous', letter: 'G', label: 'Noncontiguous', states: ['AK', 'HI'] },
];

export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const REGION_BY_STATE: Record<string, Region> = {};
for (const region of REGIONS) {
  for (const state of region.states) REGION_BY_STATE[state] = region;
}

export function regionForState(stateCode: string | null | undefined): Region | null {
  if (!stateCode) return null;
  return REGION_BY_STATE[stateCode.trim().toUpperCase()] ?? null;
}

export function stateName(stateCode: string | null | undefined): string | null {
  if (!stateCode) return null;
  const code = stateCode.trim().toUpperCase();
  return US_STATES.find((s) => s.code === code)?.name ?? null;
}

/** Zero-padded to six digits, matching the A-000101 format. */
export function formatCustomerNumber(customerNumber: number | null | undefined): string | null {
  if (customerNumber == null || !Number.isFinite(customerNumber)) return null;
  return String(customerNumber).padStart(6, '0');
}

/**
 * The job code, or null when either half is missing — a client with no state
 * has no region letter, and a lead has no customer number until it converts.
 */
export function jobCodeFor(
  stateCode: string | null | undefined,
  customerNumber: number | null | undefined
): string | null {
  const region = regionForState(stateCode);
  const number = formatCustomerNumber(customerNumber);
  if (!region || !number) return null;
  return `${region.letter}-${number}`;
}
